import React, { useState } from 'react'

import { Box, Stack, Tooltip, Typography } from '@mui/material'

import { Card, Skeleton, Switch } from '@repo/ui'

import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal/ConfirmSettingModal'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import {
  useReferralInviteRegions,
  useUpdateReferralInviteRegion,
  type ReferralInviteRegion,
} from 'hooks/api/useInternalReferrals'
import { absoluteDateTime, getErrorMessage } from 'utils'

const REGION_LABELS: Record<ReferralInviteRegion['region'], { title: string; detail: string }> = {
  eu_uk: { title: 'EU and UK', detail: 'Addresses with an EU, EEA or UK country domain, or sent by a brand based there.' },
  rest_of_world: { title: 'Rest of world', detail: 'Every other invitation.' },
}

/**
 * VRG-656 — the per-region switch for referral invitation EMAILS, without a deploy.
 *
 * Platform admins only (the PATCH is `RoleName.PlatformAdmin`); a platform manager sees the
 * state with the switch disabled and a tooltip saying why (§27). Flipping it is gated by a
 * confirm that names the effect before anything changes (§43): while a region is off, brands
 * can still invite, but nobody there is emailed and their result reads "Not sent".
 */
export const InviteRegionsCard = () => {
  const { isHasAccess: isPlatformAdmin } = useCanAccess({ role: 'platform_admin' })
  const { data: regions, isLoading } = useReferralInviteRegions()
  const update = useUpdateReferralInviteRegion()
  const { open: notify } = useSnackbar()
  const [pending, setPending] = useState<ReferralInviteRegion | null>(null)

  const confirm = async () => {
    if (!pending) return
    try {
      await update.mutateAsync({ region: pending.region, sending_enabled: !pending.sending_enabled })
      notify({
        type: 'success',
        title: `Invitation emails ${pending.sending_enabled ? 'paused' : 'resumed'} for ${REGION_LABELS[pending.region].title}.`,
      })
      setPending(null)
    } catch (e) {
      notify({ type: 'error', title: getErrorMessage(e) })
    }
  }

  return (
    <Card sx={{ p: 4, mb: 0 }}>
      <Stack spacing={4}>
        <Box>
          <Typography variant="body1" display="block">Invitation emails by region</Typography>
          <Typography variant="body3" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
            Whether a brand&apos;s &quot;Refer &amp; earn&quot; invitation is emailed. Links shared by hand are not
            affected.
          </Typography>
        </Box>
        {isLoading
          ? [0, 1].map((i) => <Skeleton key={i} variant="rounded" height={56} />)
          : (regions ?? []).map((r) => {
              const label = REGION_LABELS[r.region] ?? { title: r.region, detail: '' }
              const control = (
                <Box component="span">
                  <Switch
                    checked={r.sending_enabled}
                    disabled={!isPlatformAdmin}
                    onChange={() => setPending(r)}
                    inputProps={{ 'aria-label': `Send invitation emails to ${label.title}` }}
                  />
                </Box>
              )
              return (
                <Stack key={r.region} direction="row" spacing={4} alignItems="flex-start">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">
                      {label.title} · {r.sending_enabled ? 'Sending' : 'Paused'}
                    </Typography>
                    <Typography variant="body3" color="text.secondary" display="block" sx={{ lineHeight: 1.5 }}>
                      {label.detail} Lawful basis: {r.lawful_basis}
                    </Typography>
                    <Typography variant="body4" color="text.secondary" display="block" sx={{ mt: 1 }}>
                      Updated {absoluteDateTime(r.updated_at)}
                    </Typography>
                  </Box>
                  {isPlatformAdmin ? (
                    control
                  ) : (
                    <Tooltip title="Only platform admins can change this.">{control}</Tooltip>
                  )}
                </Stack>
              )
            })}
      </Stack>

      <ConfirmSettingModal
        open={!!pending}
        onClose={() => setPending(null)}
        onConfirm={confirm}
        loading={update.isPending}
        title={
          pending?.sending_enabled
            ? `Pause invitation emails to ${pending ? REGION_LABELS[pending.region].title : ''}?`
            : `Resume invitation emails to ${pending ? REGION_LABELS[pending.region].title : ''}?`
        }
        consequence={
          pending?.sending_enabled
            ? 'Brands can still invite people there, but no email is sent and each result reads "Not sent". Takes effect immediately.'
            : 'New invitations to this region are emailed again from now on. Invitations recorded while paused are not sent retroactively.'
        }
        confirmLabel={pending?.sending_enabled ? 'Pause emails' : 'Resume emails'}
      />
    </Card>
  )
}
